import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import { formatDateView } from './utils/dateUtils';
import { formatString } from './utils/StringUtils';
import Layout from './components/Layout';
import ResponsiveShadow from './components/ResponsiveShadow';
import '../service/css/Dashboard.css';

const Home = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const anoBase = localStorage.getItem('anoBase');
  const [demandas, setDemandas] = useState([]);
  const [catmatStats, setCatmatStats] = useState(null);
  const [catserStats, setCatserStats] = useState(null);
  const [isLoading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      navigate('/');
      return;
    }

    const headers = { Authorization: `Bearer ${token}` };

    Promise.allSettled([
      axios.get(`${API_BASE_URL.DEMANDA}/cnpj`, { params: { cnpj, anoBase }, headers }),
      axios.get(`${API_BASE_URL.CATMAT}/estatisticas`, { headers }),
      axios.get(`${API_BASE_URL.CATSER}/estatisticas`, { headers }),
    ]).then(([demandasRes, catmatRes, catserRes]) => {
      if (demandasRes.status === 'fulfilled') setDemandas(demandasRes.value.data || []);
      if (catmatRes.status === 'fulfilled') setCatmatStats(catmatRes.value.data);
      if (catserRes.status === 'fulfilled') setCatserStats(catserRes.value.data);
    }).finally(() => setLoading(false));
  }, [token, cnpj, anoBase, navigate]);

  const stats = useMemo(() => {
    const total = demandas.length;
    const totalItens = demandas.reduce((acc, d) => acc + (d.qtdItens || 0), 0);
    const demandasSemItens = demandas.filter(d => !d.qtdItens || d.qtdItens === 0).length;

    const catmatTotal = catmatStats?.totalRegistros || catmatStats?.total || 0;
    const catserTotal = catserStats?.totalRegistros || catserStats?.total || 0;

    return { total, totalItens, demandasSemItens, catmatTotal, catserTotal };
  }, [demandas, catmatStats, catserStats]);

  const recentDemandas = useMemo(() => {
    return [...demandas]
      .sort((a, b) => {
        const dateA = a.dataCriacao ? new Date(a.dataCriacao) : new Date(0);
        const dateB = b.dataCriacao ? new Date(b.dataCriacao) : new Date(0);
        return dateB - dateA;
      })
      .slice(0, 5);
  }, [demandas]);

  if (isLoading) {
    return (
      <Layout>
      <div className="dashboard">
          <div className="row g-3 mb-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="col-xl-3 col-md-6">
                <div className="skeleton" style={{ height: '120px' }}></div>
              </div>
            ))}
          </div>
          <div className="skeleton" style={{ height: '300px' }}></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <ResponsiveShadow style={{ overflow: 'hidden' }}>
        <div className="d-flex flex-column p-3" style={{ flex: 1, minHeight: 0 }}>
          {/* KPI Cards */}
          <div className="row g-3 mb-4">
          <div className="col-xl-3 col-md-6 stagger-3">
            <div className="card kpi-card kpi-gradient-green">
              <div className="card-body d-flex align-items-center gap-3">
                <div className="kpi-icon">
                  <i className="bi bi-box-seam"></i>
                </div>
                <div>
                  <div className="kpi-value">{stats.catmatTotal.toLocaleString('pt-BR')}</div>
                  <div className="kpi-label">Itens CATMAT</div>
                  <div className="kpi-sub">Catalogo de materiais</div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-md-6 stagger-4">
            <div className="card kpi-card kpi-gradient-purple">
              <div className="card-body d-flex align-items-center gap-3">
                <div className="kpi-icon">
                  <i className="bi bi-gear"></i>
                </div>
                <div>
                  <div className="kpi-value">{stats.catserTotal.toLocaleString('pt-BR')}</div>
                  <div className="kpi-label">Itens CATSER</div>
                  <div className="kpi-sub">Catalogo de servicos</div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-md-6 stagger-4">
            <div className="card kpi-card kpi-gradient-blue">
              <div className="card-body d-flex align-items-center gap-3">
                <div className="kpi-icon">
                  <i className="bi bi-file-earmark-text"></i>
                </div>
                <div>
                  <div className="kpi-value">{stats.total}</div>
                  <div className="kpi-label">Demandas</div>
                  <div className="kpi-sub">{stats.totalItens} itens total</div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-md-6 stagger-4">
            <div className="card kpi-card kpi-gradient-rose">
              <div className="card-body d-flex align-items-center gap-3">
                <div className="kpi-icon">
                  <i className="bi bi-exclamation-triangle"></i>
                </div>
                <div>
                  <div className="kpi-value">{stats.demandasSemItens}</div>
                  <div className="kpi-label">Demandas Sem Itens</div>
                  <div className="kpi-sub">Demandas pendentes</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Demandas */}
        {recentDemandas.length > 0 && (
          <div className="flex-grow-1 d-flex flex-column" style={{ minHeight: 0 }}>
            <div className="card border-0 shadow-sm d-flex flex-column" style={{ borderRadius: '16px', flex: 1, minHeight: 0 }}>
              <div className="card-body p-3 d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="section-header mb-0 border-0 pb-0">
                    <i className="bi bi-clock-history"></i>
                    Demandas Recentes
                  </div>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => navigate('/demandas')}
                    style={{ fontSize: '0.72rem', borderRadius: '8px' }}
                  >
                    Ver todas <i className="bi bi-arrow-right ms-1"></i>
                  </button>
                </div>
                <div className="flex-grow-1" style={{ minHeight: 0, overflowY: 'auto' }}>
                  <table className="table table-hover mb-0" style={{ fontSize: '0.78rem' }}>
                      <thead style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 }}>
                        <tr>
                          <th style={{ fontWeight: 600, color: '#475569', fontSize: '0.72rem' }}>Nº</th>
                          <th style={{ fontWeight: 600, color: '#475569', fontSize: '0.72rem' }}>Data</th>
                          <th style={{ fontWeight: 600, color: '#475569', fontSize: '0.72rem' }}>Tipo</th>
                          <th style={{ fontWeight: 600, color: '#475569', fontSize: '0.72rem' }}>Objeto</th>
                          <th style={{ fontWeight: 600, color: '#475569', fontSize: '0.72rem' }}>Itens</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentDemandas.map(d => (
                          <tr
                            key={`${d.cnpj}-${d.num}`}
                          >
                            <td className="fw-semibold">{formatString(d.num, 4)}</td>
                            <td>{formatDateView(d.dataCriacao)}</td>
                            <td>{d.tipo || '-'}</td>
                            <td style={{ maxWidth: '300px' }}>
                              <span className="text-truncate d-inline-block" style={{ maxWidth: '100%' }}>
                                {d.objeto || '-'}
                              </span>
                            </td>
                            <td className="text-center">{d.qtdItens || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                </div>
              </div>
            </div>
          </div>
        )}
        </div>
      </ResponsiveShadow>
    </Layout>
  );
};

export default Home;
